export const perspectusDetails = () => {
  return `## 📊 Perspectus: AI-Powered Data Analysis Tool  

**Perspectus** is an AI-powered data analysis platform that lets users **chat with their data** across multiple sources. From databases like **PostgreSQL, MySQL, and Snowflake** to files such as **Excel spreadsheets**, Perspectus helps users explore, analyze, and visualize data through natural conversation—without needing to write SQL or code. 🚀  

### 🛠️ Technologies Behind Perspectus  
- **Backend**: FastAPI, LangGraph, Pandas, SQL ⚙️  
- **Frontend**: TypeScript & Next.js ⚡  
- **Database**: PostgreSQL

### ✨ Key Features  
- **Chat with Your Data**: Ask questions in plain English and get instant answers from connected databases and files.  
- **Multi-Source Data Support**: Analyze data from **PostgreSQL, MySQL, Snowflake**, and **Excel files** in one unified interface.  
- **Automatic SQL Generation**: Convert natural language into SQL queries automatically.  
- **Interactive Visualizations**: Instantly generate charts, graphs, and insights directly from chat.  
- **File-Based Analysis**: Upload spreadsheets and explore them conversationally with AI.  
- **Context-Aware Insights**: Get summaries, trends, comparisons, and business insights from your data in seconds.  

### 🎯 Main Objectives  
1. **Make Data Analysis Accessible**: Enable users to explore and understand data without technical expertise.  
2. **Unify Data Exploration**: Allow seamless analysis across databases and files from a single chat interface.  
3. **Accelerate Decision-Making**: Turn raw data into visual insights and actionable answers quickly.  

💡 Discover trends, uncover insights, and visualize your data effortlessly with **Perspectus**—your AI analyst in a chat interface.  
`;
};
export const padelearnDetails = () => {
  return `## 📘 Padelearn: AI-Powered Learning Management System  

**Padelearn** is a cutting-edge Learning Management System (LMS) designed to enhance education through AI-driven assessments. By leveraging artificial intelligence, Padelearn helps students better understand course material with interactive quizzes and immediate feedback. 🚀  


<img src="https://i.ibb.co/DkV1gKV/Padelearn.png" style="width: 100%; margin-bottom:15px;" />


### 🛠️ Technologies Behind Padelearn  
- **Backend**: Python Django & Django REST Framework 🐍  
- **Frontend**: TypeScript & Next.js ⚡  
- **Database**: PostgreSQL 🗄️  
- **AI Integration**: Langchain for seamless LLM integration 🤖  
- **Background Tasks**: Celery & RabbitMQ for efficient processing ⏳  

### ✨ Key Features  

#### For Administration:  
1. **👥 User and Content Management**:  
   - Add, edit, or delete students and instructors.  
   - Manage trainings and courses.  

2. **🔑 Granting Permissions**:  
   - Assign trainers access to specific trainings or courses.  

#### For Trainers:  
1. **📚 Adding Course Content**:  
   - Upload and manage course materials in PDF format.  

2. **📝 Quiz Generation**:  
   - Automatically create multiple-choice quizzes from uploaded content using AI.  

#### For Students:  
1. **📖 Access to Educational Content**:  
   - View and download PDF course slides.  

2. **🎯 Participation in Quizzes**:  
   - Take AI-generated quizzes with randomly selected questions.  
   - Receive instant results to assess performance and identify areas for improvement.  

### 🎯 Main Objectives  
1. **Facilitate Learning**: Enhance understanding through interactive assessments and immediate feedback.  
2. **Simplify Management**: Streamline training and course management for trainers and administrators.  
3. **Leverage AI**: Deliver personalized and effective learning experiences with the power of artificial intelligence.  

💡 Transform the way you learn and teach with **Padelearn**, powered by AI!  

`;
};

export const embuildDetails = () => {
  return `## ✉️ Embuild: Drag & Drop Email Builder  

**Embuild** is an Drag & Drop Email Builder designed to make creating emails quick and easy. With its user-friendly interface, you can design beautiful email without any coding knowledge. 🚀  

### 🛠️ Technologies Behind Embuild  
- **Frontend**: TypeScript & Next.js ⚡  

### ✨ Key Features  
- **Drag & Drop Interface**: Easily build email templates by dragging and dropping components.    
- **Responsive Design**: Ensure your emails look great on any device with responsive design capabilities.    
- **Real-time Preview**: See your email design in real-time as you build it.  

### 🎯 Main Objectives  
1. **Simplify Email Creation**: Allow users to create professional emails without coding.  
2. **Improve Efficiency**: Save time by using drag-and-drop functionality for building and customizing email templates.  
3. **Responsive Design**: Create emails that look perfect on both desktop and mobile devices.  

💡 Build beautiful, responsive emails effortlessly with **Embuild**—the easiest way to design emails!  
`;
};

export const smsDetails = () => {
  return `## 🌟 Sensor Monitoring System (SMS)  

**Sensor Monitoring System (SMS)** is an advanced platform for visualizing data collected from sensors in real-time. 🚀 The system supports monitoring metrics such as **temperature**, **water tank levels**, and **humidity** with sleek and interactive visualizations.  

### 🛠️ Technologies Used  
- **Hardware**: Arduino for sensor data collection and transmission.  
- **Protocols**:  
  - **WebSocket** for real-time data updates.  
  - **MQTT** for efficient data publishing and communication.  
- **Software Stack**:  
  - **Backend**: Python & Django 🐍  
  - **Frontend**: TypeScript & Next.js ⚡  

### ✨ Features  
- **📊 Real-time Metrics**: Stay updated with live sensor data.  
- **📈 Time Series Graphs**: Visualize historical trends effortlessly.  
- **⚡ Seamless Integration**: Combines powerful hardware and software for a smooth user experience.  

### 🚀 Why SMS?  
With SMS, you can make data-driven decisions by monitoring environmental metrics in real-time, making it ideal for industries, smart homes, and research projects.  

💡 Start monitoring smarter today with **SMS**!  
`;
};

export const geneteraDetails = () => {
  return `## 🤖 GENETERA AI  

**GENETERA AI** is a cutting-edge platform that leverages artificial intelligence to revolutionize content creation. 🌟 Users can upload their documents and use the tool to generate AI-driven content enriched with context from their uploaded files.  

  

### ✨ Key Features  
- **📝 AI Content Creation**: Generate high-quality, context-aware content powered by advanced AI models.  
- **📚 Knowledge Base**: Manage and access uploaded documents in an organized repository.  
- **👥 Team Collaboration**: Work together with your team on shared content effortlessly.  
- **🔐 Permissions Control**: Assign and manage user permissions for secure and controlled access.  

### 🛠️ Technologies Behind GENETERA AI  
- **Backend**: Python & Django 🐍  
- **Frontend**: TypeScript & Next.js ⚡  
- **Database**: PostgreSQL 🗄️  
- **Background Tasks**: Celery & Redis 🚀

💡 Transform your content creation process today with **GENETERA AI**!  
`;
};

export const ihuriroDetails = () => {
  return `## 🌐 IHURIRO  

**IHURIRO** is an all-in-one platform designed to simplify connections and solve everyday challenges. 🚀 Whether you're buying or selling secondhand products or reconnecting lost documents with their owners, IHURIRO is here to make it easier and more efficient.  

 

### ✨ Key Features  
1. **🛒 Connect Buyers and Sellers**:  
   - A marketplace for secondhand products.  
   - Easy-to-use platform to list, search, and buy items.  

2. **📄 Document Matching**:  
   - Helps reconnect lost documents with their rightful owners.  
   - Advanced matching algorithms to ensure accurate results.  

4. **🔔 Notifications**:  
   - Real-time alerts for document matches or new product listings.  
   - Keep users informed and engaged.  

### 🛠️ Technologies Behind IHURIRO  
- **Backend**: PHP  
- **Database**: MySQL 🗄️  
- **Frontend**: JavaScript (jQuery) & Bootstrap 🎨 
`;
};

export const dnmToolDetails = () => {
  return `## 🌐 Domain Name Manager (DNM)  

**Domain Name Manager (DNM)** is a powerful tool designed to manage and visualize domain name data effortlessly. Whether you input data through a form or integrate via API, DNM provides robust capabilities for streamlined domain management. 🚀  


### ✨ Key Features  
1. **📊 Domain Data Visualization**:  
   - Interactive and clear visual representations of domain name data.  
   - Gain insights and trends at a glance.  

2. **🛠️ Domain Management**:  
   - Add, update, and organize domain name records with ease.  
   - Flexible data input via form or API integration.  

3. **⚡ Efficient Background Processing**:  
   - Time-consuming tasks are offloaded to Celery for optimized performance.  
   - Ensures smooth and responsive user interactions.  

4. **🔍 Search and Filter**:  
   - Quickly locate domain records with powerful search and filter options.  

### 🛠️ Technologies Behind DNM  
- **Backend**: Python & Django 🐍  
- **Frontend**: Django Template Language, HTML, CSS 🎨  
- **Database**: PostgreSQL 🗄️  
- **Background Tasks**: Celery for handling time-consuming computations ⏳  
`;
};
